// src/admin/DataMigration/tools/CatechistIdTool.js
import React, { useState } from 'react';
import { collection, getDocs, writeBatch, doc } from 'firebase/firestore';
import { firestore } from '../../../firebaseConfig';
import { generateCatechistId } from '../utils/migrationHelpers';

const CatechistIdTool = ({ setProgress, setStatus, setTerminalLog, isProcessing }) => {
  const [isScanning, setIsScanning] = useState(false);
  const [catechistList, setCatechistList] = useState([]);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [sortBy, setSortBy] = useState('old_id'); // 'old_id' | 'name' | 'class' | 'original'
  const [padZero, setPadZero] = useState(true);   // '01' vs '1'

  const getFirstName = (fullName) => {
    if (!fullName) return '';
    const parts = fullName.trim().split(/\s+/);
    return parts[parts.length - 1];
  };

  const handleScanCatechists = async () => {
    try {
      setIsScanning(true);
      setTerminalLog(`🔍 Đang tải danh sách Giáo lý viên từ collection 'staff'...\n`);

      const staffSnap = await getDocs(collection(firestore, 'staff'));
      if (staffSnap.empty) {
        alert('Không tìm thấy dữ liệu giáo lý viên trong collection staff.');
        setIsScanning(false);
        return;
      }

      let rawList = staffSnap.docs.map(d => ({
        uid: d.id,
        ...d.data()
      }));

      // --- LOGIC SẮP XẾP THỨ TỰ ---
      if (sortBy === 'old_id') {
        // Sắp xếp tự nhiên theo ID cũ (Hỗ trợ tốt dạng chuỗi có số: 1, 2... 10 hoặc 01 - ...)
        rawList.sort((a, b) => {
          const idA = String(a.idCode || '').trim();
          const idB = String(b.idCode || '').trim();
          if (!idA && !idB) return 0;
          if (!idA) return 1; // Chưa có ID cũ thì đẩy xuống dưới cùng
          if (!idB) return -1;
          return idA.localeCompare(idB, undefined, { numeric: true, sensitivity: 'base' });
        });
      } else if (sortBy === 'name') {
        rawList.sort((a, b) => {
          const fnA = getFirstName(a.fullName || '');
          const fnB = getFirstName(b.fullName || '');
          return fnA.localeCompare(fnB, 'vi');
        });
      } else if (sortBy === 'class') {
        rawList.sort((a, b) => (a.className || '').localeCompare(b.className || '', 'vi'));
      }

      // Đánh số thứ tự và sinh ID mới không khoảng trắng, không gạch ngang
      const mappedList = rawList.map((c, index) => {
        const newIdCode = generateCatechistId(index + 1, c.holyName, c.fullName, padZero);
        return {
          stt: index + 1,
          uid: c.uid,
          holyName: c.holyName || '',
          fullName: c.fullName || '',
          className: c.className || 'Chưa phân lớp',
          currentId: c.idCode || '(Chưa có)',
          newId: newIdCode,
          isChanged: (c.idCode || '') !== newIdCode
        };
      });

      setCatechistList(mappedList);
      setTerminalLog(prev => prev + `✓ Đã quét ${mappedList.length} GLV (Thứ tự: ${sortBy === 'old_id' ? 'Theo ID cũ' : sortBy}).\n`);
      setShowPreviewModal(true);
    } catch (err) {
      console.error('Lỗi khi nạp GLV:', err);
      alert('Lỗi nạp dữ liệu: ' + err.message);
    } finally {
      setIsScanning(false);
    }
  };

  const handleConfirmUpdate = async () => {
    if (catechistList.length === 0) return;

    setShowPreviewModal(false);
    setStatus('processing');
    setProgress(1);
    setTerminalLog(`🚀 Bắt đầu cập nhật ID mới cho ${catechistList.length} Giáo lý viên...\n`);

    try {
      const total = catechistList.length;
      const CHUNK_SIZE = 50;
      let totalUpdated = 0;

      for (let i = 0; i < total; i += CHUNK_SIZE) {
        const chunk = catechistList.slice(i, i + CHUNK_SIZE);
        const batch = writeBatch(firestore);

        chunk.forEach(item => {
          const staffRef = doc(firestore, 'staff', item.uid);
          batch.update(staffRef, {
            idCode: item.newId,
            updatedAt: new Date().toISOString()
          });
        });

        await batch.commit();
        totalUpdated += chunk.length;
        const currentPct = Math.round((totalUpdated / total) * 100);

        setProgress(currentPct);
        setTerminalLog(prev => prev + `  + [Đã lưu lô]: ${totalUpdated}/${total} GLV (${currentPct}%)\n`);
      }

      setProgress(100);
      setStatus('done');
      setTerminalLog(prev => prev + `\n🎉 TỔNG KẾT: Đã cập nhật thành công ID mới cho ${totalUpdated} Giáo lý viên vào Firestore!\n`);
    } catch (err) {
      console.error('Lỗi khi cập nhật ID GLV:', err);
      setStatus('error');
      setTerminalLog(prev => prev + `❌ LỖI LƯU ID GLV: ${err.message}\n`);
    }
  };

  return (
    <div className="dmm-att-section-card">
      <div className="dmm-att-controls">
        <div className="dmm-select-group">
          <label>Thứ tự đánh số STT:</label>
          <select 
            value={sortBy} 
            onChange={(e) => setSortBy(e.target.value)}
            className="dmm-year-select"
            disabled={isProcessing || isScanning}
          >
            <option value="old_id">📌 Dựa trên ID cũ (Tăng dần)</option>
            <option value="name">Sắp xếp theo Tên (A - Z)</option>
            <option value="class">Sắp xếp theo Khối / Lớp</option>
            <option value="original">Theo thứ tự Firestore hiện tại</option>
          </select>
        </div>

        <div className="dmm-select-group" style={{ maxWidth: '180px' }}>
          <label>Định dạng số:</label>
          <select 
            value={padZero ? 'padded' : 'normal'} 
            onChange={(e) => setPadZero(e.target.value === 'padded')}
            className="dmm-year-select"
            disabled={isProcessing || isScanning}
          >
            <option value="padded">Có số 0 (01, 02...)</option>
            <option value="normal">Không số 0 (1, 2...)</option>
          </select>
        </div>

        <button
          onClick={handleScanCatechists}
          disabled={isProcessing || isScanning}
          className="dmm-execute-btn"
        >
          {isScanning ? '⏳ Đang quét...' : '🔍 Quét & Xem Trước ID'}
        </button>
      </div>

      {/* MODAL XEM TRƯỚC VÀ XÁC NHẬN */}
      {showPreviewModal && (
        <div className="dmm-modal-backdrop">
          <div className="dmm-modal-card" style={{ maxWidth: '850px' }}>
            <div className="dmm-modal-header">
              <h3>🆔 BẢN XEM TRƯỚC ĐỔI ID GIÁO LÝ VIÊN</h3>
              <button className="dmm-modal-close-btn" onClick={() => setShowPreviewModal(false)}>✕</button>
            </div>

            <div className="dmm-modal-body">
              <p className="dmm-modal-intro">
                Cấu trúc ID mới viết liền: <strong>Số + Chữ đầu Tên Thánh + Các chữ đầu Họ và Tên</strong> (Ví dụ: <code>01GNVA</code>, <code>02TTM</code>). Trường <code>idCode</code> trong collection <code>staff</code> sẽ được cập nhật đồng loạt.
              </p>

              <div className="dmm-stats-grid">
                <div className="dmm-stat-card highlight">
                  <span className="stat-label">Tổng Số GLV</span>
                  <span className="stat-value">{catechistList.length}</span>
                </div>
                <div className="dmm-stat-card">
                  <span className="stat-label">Cần Cập Nhật ID</span>
                  <span className="stat-value">{catechistList.filter(c => c.isChanged).length}</span>
                </div>
                <div className="dmm-stat-card">
                  <span className="stat-label">Mẫu ID Mới (Số 1)</span>
                  <span className="stat-value" style={{ fontSize: '16px', marginTop: '4px' }}>
                    {catechistList[0]?.newId || '-'}
                  </span>
                </div>
              </div>

              <div className="dmm-preview-table-wrapper">
                <table className="dmm-preview-table">
                  <thead>
                    <tr>
                      <th>STT Mới</th>
                      <th>Tên Thánh</th>
                      <th>Họ và Tên</th>
                      <th>Lớp / Khối</th>
                      <th>ID Hiện Tại (Cũ)</th>
                      <th>ID Mới Sẽ Cập Nhật</th>
                    </tr>
                  </thead>
                  <tbody>
                    {catechistList.map((item) => (
                      <tr key={item.uid}>
                        <td>{item.stt}</td>
                        <td>{item.holyName}</td>
                        <td><strong>{item.fullName}</strong></td>
                        <td>{item.className}</td>
                        <td style={{ color: '#64748b' }}>{item.currentId}</td>
                        <td>
                          <span className="td-code" style={{ backgroundColor: '#eff6ff', padding: '3px 8px', borderRadius: '4px', letterSpacing: '0.5px' }}>
                            {item.newId}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="dmm-modal-footer">
              <button onClick={() => setShowPreviewModal(false)} className="dmm-btn-cancel">
                ✕ Hủy Bỏ
              </button>
              <button onClick={handleConfirmUpdate} className="dmm-btn-confirm">
                🚀 Xác Nhận Cập Nhật ID Cho Toàn Bộ GLV
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CatechistIdTool;